/**
 * themeEngine.ts
 *
 * Master Theme Engine for the Sovereign Mesh Platform (Sprint THEME-01).
 * Defines comprehensive semantic tokens across 4 calibrated presets (Dark & Light)
 * and broadcasts theme changes to embedded micro-apps.
 */

export type ThemePreset = 'midnight' | 'dracula' | 'catppuccin' | 'nordic';

export interface ThemeTokens {
  readonly name: string;
  readonly isDark: boolean;

  // ── Surfaces & Backgrounds ───────────────────────────────────────────────
  readonly bgMain: string;          // --theme-bg-main (Application canvas)
  readonly bgHeader: string;        // --theme-bg-header (Top nav bar)
  readonly bgSidebar: string;       // --theme-bg-sidebar (Navigation drawer)
  readonly bgCard: string;          // --theme-bg-card (Cards, containers)
  readonly bgCardHover: string;     // --theme-bg-card-hover
  readonly bgInput: string;         // --theme-bg-input (Inputs, controls)
  readonly bgModal: string;         // --theme-bg-modal (Dialog overlays)
  readonly bgModalBackdrop: string;  // --theme-bg-backdrop (Modal dimmed backdrop)
  readonly bgSubtle: string;        // --theme-bg-subtle (Tags, gutters, tracks)

  // ── Borders & Focus Rings ────────────────────────────────────────────────
  readonly border: string;          // --theme-border (Default subtle border)
  readonly borderHighlight: string; // --theme-border-highlight (Active border)
  readonly borderSubtle: string;    // --theme-border-subtle (Dividers)
  readonly focusRing: string;       // --theme-focus-ring (WCAG 3:1 focus ring)

  // ── Typography & Content ─────────────────────────────────────────────────
  readonly textPrimary: string;     // --theme-text-primary (Headings, titles ≥4.5:1)
  readonly textSecondary: string;   // --theme-text-secondary (Body, labels ≥4.5:1)
  readonly textMuted: string;       // --theme-text-muted (Captions, timestamps)
  readonly textInverse: string;     // --theme-text-inverse (High contrast on accent)

  // ── Brand Accents & Interactive ──────────────────────────────────────────
  readonly accent: string;          // --theme-accent (Primary brand color)
  readonly accentHover: string;     // --theme-accent-hover
  readonly accentSubtle: string;    // --theme-accent-subtle (12% tint)
  readonly iconIdle: string;        // --theme-icon-idle
  readonly iconActive: string;      // --theme-icon-active

  // ── Semantic Status Tokens ───────────────────────────────────────────────
  readonly statusSuccessBg: string;   // --theme-status-success-bg
  readonly statusSuccessText: string; // --theme-status-success-text (≥4.5:1 vs bg)
  readonly statusWarningBg: string;   // --theme-status-warning-bg
  readonly statusWarningText: string; // --theme-status-warning-text (≥4.5:1 vs bg)
  readonly statusDangerBg: string;    // --theme-status-danger-bg
  readonly statusDangerText: string;  // --theme-status-danger-text (≥4.5:1 vs bg)
  readonly statusInfoBg: string;      // --theme-status-info-bg
  readonly statusInfoText: string;    // --theme-status-info-text (≥4.5:1 vs bg)

  // ── Palette Accents ──────────────────────────────────────────────────────
  readonly cyan: string;
  readonly indigo: string;
  readonly emerald: string;
  readonly amber: string;
  readonly rose: string;
}

export const THEME_PRESETS: Record<ThemePreset, ThemeTokens> = {
  midnight: {
    name: '🌙 Midnight Cyber (Default)',
    isDark: true,
    bgMain: '#0b0f19',
    bgHeader: '#0f172a',
    bgSidebar: '#0b0f19',
    bgCard: '#151d2a',
    bgCardHover: '#1e293b',
    bgInput: '#1e293b',
    bgModal: '#0f172a',
    bgModalBackdrop: 'rgba(0, 0, 0, 0.78)',
    bgSubtle: '#020617',
    border: '#1e293b',
    borderHighlight: '#38bdf8',
    borderSubtle: 'rgba(255, 255, 255, 0.08)',
    focusRing: '#38bdf8',
    textPrimary: '#f8fafc',
    textSecondary: '#cbd5e1',
    textMuted: '#94a3b8',
    textInverse: '#0b0f19',
    accent: '#38bdf8',
    accentHover: '#0284c7',
    accentSubtle: 'rgba(56, 189, 248, 0.12)',
    iconIdle: '#94a3b8',
    iconActive: '#38bdf8',
    statusSuccessBg: 'rgba(16, 185, 129, 0.15)',
    statusSuccessText: '#34d399',
    statusWarningBg: 'rgba(245, 158, 11, 0.15)',
    statusWarningText: '#fbbf24',
    statusDangerBg: 'rgba(244, 63, 94, 0.15)',
    statusDangerText: '#fb7185',
    statusInfoBg: 'rgba(56, 189, 248, 0.15)',
    statusInfoText: '#38bdf8',
    cyan: '#06b6d4',
    indigo: '#6366f1',
    emerald: '#10b981',
    amber: '#f59e0b',
    rose: '#f43f5e',
  },
  dracula: {
    name: '🧛 Dracula Dark',
    isDark: true,
    bgMain: '#1e1e2e',
    bgHeader: '#282a36',
    bgSidebar: '#181826',
    bgCard: '#282a36',
    bgCardHover: '#383a4c',
    bgInput: '#44475a',
    bgModal: '#282a36',
    bgModalBackdrop: 'rgba(0, 0, 0, 0.8)',
    bgSubtle: '#191a21',
    border: '#44475a',
    borderHighlight: '#bd93f9',
    borderSubtle: 'rgba(255, 255, 255, 0.1)',
    focusRing: '#bd93f9',
    textPrimary: '#f8f8f2',
    textSecondary: '#a6adc8',
    textMuted: '#6272a4',
    textInverse: '#1e1e2e',
    accent: '#bd93f9',
    accentHover: '#ff79c6',
    accentSubtle: 'rgba(189, 147, 249, 0.15)',
    iconIdle: '#6272a4',
    iconActive: '#bd93f9',
    statusSuccessBg: 'rgba(80, 250, 123, 0.15)',
    statusSuccessText: '#50fa7b',
    statusWarningBg: 'rgba(241, 250, 140, 0.15)',
    statusWarningText: '#f1fa8c',
    statusDangerBg: 'rgba(255, 85, 85, 0.15)',
    statusDangerText: '#ff5555',
    statusInfoBg: 'rgba(139, 233, 253, 0.15)',
    statusInfoText: '#8be9fd',
    cyan: '#8be9fd',
    indigo: '#bd93f9',
    emerald: '#50fa7b',
    amber: '#f1fa8c',
    rose: '#ff5555',
  },
  catppuccin: {
    name: '🌌 Catppuccin Macchiato',
    isDark: true,
    bgMain: '#181825',
    bgHeader: '#1e1e2e',
    bgSidebar: '#11111b',
    bgCard: '#1e1e2e',
    bgCardHover: '#2a2b3d',
    bgInput: '#313244',
    bgModal: '#1e1e2e',
    bgModalBackdrop: 'rgba(0, 0, 0, 0.78)',
    bgSubtle: '#11111b',
    border: '#313244',
    borderHighlight: '#89b4fa',
    borderSubtle: 'rgba(255, 255, 255, 0.08)',
    focusRing: '#89b4fa',
    textPrimary: '#cdd6f4',
    textSecondary: '#a6adc8',
    textMuted: '#6c7086',
    textInverse: '#181825',
    accent: '#89b4fa',
    accentHover: '#b4befe',
    accentSubtle: 'rgba(137, 180, 250, 0.15)',
    iconIdle: '#a6adc8',
    iconActive: '#89b4fa',
    statusSuccessBg: 'rgba(166, 227, 161, 0.15)',
    statusSuccessText: '#a6e3a1',
    statusWarningBg: 'rgba(249, 226, 175, 0.15)',
    statusWarningText: '#f9e2af',
    statusDangerBg: 'rgba(243, 139, 168, 0.15)',
    statusDangerText: '#f38ba8',
    statusInfoBg: 'rgba(137, 220, 235, 0.15)',
    statusInfoText: '#89dceb',
    cyan: '#89dceb',
    indigo: '#b4befe',
    emerald: '#a6e3a1',
    amber: '#f9e2af',
    rose: '#f38ba8',
  },
  nordic: {
    name: '☀️ Nordic Dawn (Light)',
    isDark: false,
    bgMain: '#f8fafc',
    bgHeader: '#ffffff',
    bgSidebar: '#f1f5f9',
    bgCard: '#ffffff',
    bgCardHover: '#f8fafc',
    bgInput: '#f1f5f9',
    bgModal: '#ffffff',
    bgModalBackdrop: 'rgba(15, 23, 42, 0.65)',
    bgSubtle: '#f1f5f9',
    border: '#e2e8f0',
    borderHighlight: '#0284c7',
    borderSubtle: '#e2e8f0',
    focusRing: '#0284c7',
    textPrimary: '#0f172a',
    textSecondary: '#334155',
    textMuted: '#64748b',
    textInverse: '#ffffff',
    accent: '#0284c7',
    accentHover: '#0369a1',
    accentSubtle: 'rgba(2, 132, 199, 0.1)',
    iconIdle: '#64748b',
    iconActive: '#0284c7',
    statusSuccessBg: 'rgba(22, 163, 74, 0.12)',
    statusSuccessText: '#15803d',
    statusWarningBg: 'rgba(217, 119, 6, 0.12)',
    statusWarningText: '#b45309',
    statusDangerBg: 'rgba(220, 38, 38, 0.12)',
    statusDangerText: '#b91c1c',
    statusInfoBg: 'rgba(2, 132, 199, 0.12)',
    statusInfoText: '#0369a1',
    cyan: '#0284c7',
    indigo: '#4f46e5',
    emerald: '#16a34a',
    amber: '#d97706',
    rose: '#dc2626',
  },
};

export function applyThemeTokens(themeKey: ThemePreset): void {
  if (typeof document === 'undefined' || !document.documentElement) return;
  const tokens = THEME_PRESETS[themeKey] || THEME_PRESETS.midnight;
  const root = document.documentElement;

  root.dataset.theme = themeKey;

  // Surfaces & Backgrounds
  root.style.setProperty('--theme-bg-main', tokens.bgMain);
  root.style.setProperty('--theme-bg-header', tokens.bgHeader);
  root.style.setProperty('--theme-bg-sidebar', tokens.bgSidebar);
  root.style.setProperty('--theme-bg-card', tokens.bgCard);
  root.style.setProperty('--theme-bg-card-hover', tokens.bgCardHover);
  root.style.setProperty('--theme-bg-input', tokens.bgInput);
  root.style.setProperty('--theme-bg-modal', tokens.bgModal);
  root.style.setProperty('--theme-bg-backdrop', tokens.bgModalBackdrop);
  root.style.setProperty('--theme-bg-subtle', tokens.bgSubtle);

  // Borders & Focus
  root.style.setProperty('--theme-border', tokens.border);
  root.style.setProperty('--theme-border-highlight', tokens.borderHighlight);
  root.style.setProperty('--theme-border-subtle', tokens.borderSubtle);
  root.style.setProperty('--theme-focus-ring', tokens.focusRing);

  // Text
  root.style.setProperty('--theme-text-primary', tokens.textPrimary);
  root.style.setProperty('--theme-text-secondary', tokens.textSecondary);
  root.style.setProperty('--theme-text-muted', tokens.textMuted);
  root.style.setProperty('--theme-text-inverse', tokens.textInverse);

  // Brand & Accents
  root.style.setProperty('--theme-accent', tokens.accent);
  root.style.setProperty('--theme-accent-hover', tokens.accentHover);
  root.style.setProperty('--theme-accent-subtle', tokens.accentSubtle);
  root.style.setProperty('--theme-icon-idle', tokens.iconIdle);
  root.style.setProperty('--theme-icon-active', tokens.iconActive);

  // Status Pairs
  root.style.setProperty('--theme-status-success-bg', tokens.statusSuccessBg);
  root.style.setProperty('--theme-status-success-text', tokens.statusSuccessText);
  root.style.setProperty('--theme-status-warning-bg', tokens.statusWarningBg);
  root.style.setProperty('--theme-status-warning-text', tokens.statusWarningText);
  root.style.setProperty('--theme-status-danger-bg', tokens.statusDangerBg);
  root.style.setProperty('--theme-status-danger-text', tokens.statusDangerText);
  root.style.setProperty('--theme-status-info-bg', tokens.statusInfoBg);
  root.style.setProperty('--theme-status-info-text', tokens.statusInfoText);

  // Palette
  root.style.setProperty('--theme-cyan', tokens.cyan);
  root.style.setProperty('--theme-indigo', tokens.indigo);
  root.style.setProperty('--theme-emerald', tokens.emerald);
  root.style.setProperty('--theme-amber', tokens.amber);
  root.style.setProperty('--theme-rose', tokens.rose);

  // Inform embedded IFrames / Plugins
  broadcastThemeToIframes(tokens);

  // Dispatch reactive theme change event for sandboxed micro-frontend modules
  if (typeof window !== 'undefined') {
    const contract = getThemeTokensContract(themeKey);
    window.dispatchEvent(
      new CustomEvent('herx:theme:change', {
        detail: { preset: themeKey, tokens: contract },
      })
    );
  }
}

export function getThemeTokensContract(preset: ThemePreset = 'midnight'): import('../../packages/module-sdk/src/types.js').ThemeTokensContract {
  const tokens = THEME_PRESETS[preset] || THEME_PRESETS.midnight;
  return {
    bgApp: tokens.bgMain,
    bgCard: tokens.bgCard,
    bgInput: tokens.bgInput,
    bgHover: tokens.bgCardHover,
    textPrimary: tokens.textPrimary,
    textSecondary: tokens.textSecondary,
    textMuted: tokens.textMuted,
    border: tokens.border,
    borderSubtle: tokens.borderSubtle,
    accent: tokens.accent,
    accentHover: tokens.accentHover,
    success: tokens.statusSuccessText,
    warning: tokens.statusWarningText,
    error: tokens.statusDangerText,
    cssVariables: {
      '--theme-bg-main': tokens.bgMain,
      '--theme-bg-card': tokens.bgCard,
      '--theme-bg-input': tokens.bgInput,
      '--theme-bg-hover': tokens.bgCardHover,
      '--theme-border': tokens.border,
      '--theme-border-highlight': tokens.borderHighlight,
      '--theme-border-subtle': tokens.borderSubtle,
      '--theme-text-primary': tokens.textPrimary,
      '--theme-text-secondary': tokens.textSecondary,
      '--theme-text-muted': tokens.textMuted,
      '--theme-text-inverse': tokens.textInverse,
      '--theme-accent': tokens.accent,
      '--theme-accent-hover': tokens.accentHover,
      '--theme-status-success-bg': tokens.statusSuccessBg,
      '--theme-status-success-text': tokens.statusSuccessText,
      '--theme-status-warning-bg': tokens.statusWarningBg,
      '--theme-status-warning-text': tokens.statusWarningText,
      '--theme-status-danger-bg': tokens.statusDangerBg,
      '--theme-status-danger-text': tokens.statusDangerText,
    },
  };
}

export function getInitialTheme(): ThemePreset {
  if (typeof window === 'undefined') return 'midnight';
  const saved = localStorage.getItem('herx_theme_preset') as ThemePreset;
  if (saved && THEME_PRESETS[saved]) return saved;
  return 'midnight';
}

export function saveThemePreference(themeKey: ThemePreset): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem('herx_theme_preset', themeKey);
}

function broadcastThemeToIframes(tokens: ThemeTokens): void {
  if (typeof document === 'undefined') return;
  const iframes = document.querySelectorAll('iframe');
  iframes.forEach((iframe) => {
    try {
      iframe.contentWindow?.postMessage(
        {
          type: 'THEME_SYNC',
          payload: tokens,
        },
        '*'
      );
    } catch {}
  });
}
